import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import {
    EBON_TYPE_DOWNLOAD_CODE,
    EBON_TYPE_LINK_CODE,
    EBON_TYPE_PRE_DOWNLOADED_LINK,
    ENTERPRISE_CARD_NAME
} from '../../../../constants/ebon';
import { STATIC_ROOT } from '../../../../constants';
import { WAITING_STOCK } from '../../../../services/checkout/constants';

import {
    fetchAccountEbonOrderByIdWithCode,
    updateEbonUserState
} from '../../../../services/account/ebons/actions';

import { formatAmountCurrency } from '../../../../utils';
import { convertDate } from '../../../../utils/dateUtils';
import { extractOrderId } from '../../../../utils/ebonStatusUtils';
import { convertHrefLink, convertHtmlToReactDomWithParams } from '../../../../utils/htmlUtils';

import { useEbonOrder } from '../../../../hooks/useEbonOrder';
import { useEbonDownload } from '../../../../hooks/useEbonDownload';

import SvgComponent from '../../../SvgComponent';
import EnterpriseCardSteps from './EnterpriseCardSteps';
import ButtonComponent from '../../../button/ButtonComponent';
import { LoadingComponent } from '../../../loading/LoadingComponent';
import EbonStatusBadge from './EbonStatusBadge';
import EbonStatusUpdateForm from './EbonStatusUpdateForm';

import './EbonOrderCard.scss';

const DOWNLOAD_CODE_TYPE = [EBON_TYPE_DOWNLOAD_CODE, EBON_TYPE_PRE_DOWNLOADED_LINK];

const EbonOrderCard = ({
    fetchAccountEbonOrderByIdWithCode,
    updateEbonUserState,
    i18nPage,
    BRAND_NAME_ALT,
    items,
    id,
    vendor,
    state,
    date,
    ebonOrderWithCodes,
    ebonList: ebonListFromProps,
    token,
    paymentRequest,
    isEnterpriseCard
}) => {
    const {
        open,
        setOpen,
        showHowTo,
        setshowHowTo,
        ebonStatus,
        remainingBalance,
        submitting,
        orderStatus,
        ebonList,
        handleSubmitStatus,
        stateOk
    } = useEbonOrder({
        id,
        ebonOrderWithCodes,
        ebonListFromProps,
        fetchAccountEbonOrderByIdWithCode,
        updateEbonUserState,
        state
    });

    const { fetchingCodes, fetchingBill, downloadCodes, downloadBill } = useEbonDownload(token);

    const total = items.reduce(
        (acc, { stock, value }) => ({
            stock: acc.stock + stock,
            value: acc.value + value
        }),
        { stock: 0, value: 0 }
    );
    const totalRemainingBalance = items.reduce((acc, curr) => acc + curr.remainingBalance, 0);

    const hasEbonLink = ebonListWithCodes => {
        return (
            ebonListWithCodes &&
            ebonListWithCodes.some(
                ebon => ebon && ebon.ebonCodeDto && ebon.ebonCodeDto.type === EBON_TYPE_LINK_CODE
            )
        );
    };

    const getCodeActions = ebonListWithCodes => {
        let actionArray = [];

        if (
            ebonListWithCodes &&
            Array.isArray(ebonListWithCodes) &&
            ebonListWithCodes.some(
                ebon =>
                    ebon && ebon.ebonCodeDto && DOWNLOAD_CODE_TYPE.includes(ebon.ebonCodeDto.type)
            )
        ) {
            const nbEbon = ebonListWithCodes.filter(
                ebon =>
                    ebon && ebon.ebonCodeDto && DOWNLOAD_CODE_TYPE.includes(ebon.ebonCodeDto.type)
            );
            const totalDownload = nbEbon.reduce((a, v) => (a += v.facialValue || 0), 0);
            actionArray.push({
                label: `${
                    nbEbon.length > 1 ? i18nPage.DONWLOAD_CODES : i18nPage.DONWLOAD_CODE
                } (${formatAmountCurrency(totalDownload)})`,
                action: () => downloadCodes(id)
            });
        }

        return actionArray;
    };

    const displayOrderId = extractOrderId(id);

    return (
        <div className="order-container">
            <div id={`order-${displayOrderId}`} className="card-title">
                {`${i18nPage.ORDER}${displayOrderId}`}{' '}
                {state === WAITING_STOCK && (
                    <span className="waiting-stock">{i18nPage.WAITING_STOCK}</span>
                )}
                <EbonStatusBadge
                    status={orderStatus.status}
                    remainingBalance={orderStatus.remainingBalance}
                    totalAmount={total.value}
                />
            </div>
            <dl className="card-wrapper">
                <div className="description-list">
                    <div className="description-container">
                        <dt>{i18nPage.ORDER_DATE}</dt>
                        <dd className="details">{convertDate(date)}</dd>
                    </div>
                    <div className="description-container">
                        <dt>{i18nPage.VENDOR}</dt>
                        <dd className="details">{vendor ? vendor : '...'}</dd>
                    </div>
                    <div className="description-container">
                        <dt>{i18nPage.ORDER_TOTAL}</dt>
                        <dd className="details">{formatAmountCurrency(total.value)}</dd>
                    </div>
                    {isEnterpriseCard && (
                        <div className="description-container">
                            <dt>{i18nPage.ENTERPRISE_CARD_REMAINING}</dt>
                            <dd className="details remaining">
                                {formatAmountCurrency(totalRemainingBalance)}
                            </dd>
                        </div>
                    )}
                </div>
                <div className="button-container">
                    <button
                        type="button"
                        aria-describedby={`order-${displayOrderId}`}
                        aria-expanded={open}
                        aria-controls={`order-details-${displayOrderId}`}
                        aria-label={open ? i18nPage.CLOSE : i18nPage.OPEN}
                        onClick={setOpen}>
                        <SvgComponent
                            src={`${STATIC_ROOT}/fleche.svg`}
                            className={`chevron ${open ? 'haut' : 'bas'}`}
                            width="20"
                            height="20"
                            aria-hidden="true"
                            focusable="false"
                        />
                    </button>
                </div>
            </dl>
            {open && (
                <div id={`order-details-${displayOrderId}`}>
                    <div className="tableContainer">
                        <table className="tableStyle" aria-label={`${i18nPage.ORDER}${displayOrderId}`}>
                            <thead>
                                <tr className="tableRowHeader">
                                    <th scope="col">
                                        <span className="tableHeaderStyle">
                                            {i18nPage.EBON.COMMAND}
                                        </span>
                                    </th>
                                    <th scope="col">
                                        <span className="tableHeaderStyle">
                                            {i18nPage.EBON.AMOUNT}
                                        </span>
                                    </th>
                                    <th scope="col">
                                        <span className="tableHeaderStyle">
                                            {i18nPage.EBON.EXPIRATION}
                                        </span>
                                    </th>
                                    <th scope="col">
                                        <span className="tableHeaderStyle">
                                            {i18nPage.EBON.STATUS}
                                        </span>
                                    </th>
                                    {hasEbonLink(ebonList) && (
                                        <th scope="col">
                                            <span className="tableHeaderStyle">
                                                {i18nPage.EBON.LINK}
                                            </span>
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {ebonList && ebonList.length > 0
                                    ? ebonList.map((ebon, index) => {
                                          const formattedValue = ebon.facialValue
                                              .toFixed(2)
                                              .replace('.', ',');
                                          const showNumber =
                                              ebonList.length > 1 &&
                                              ebonList.filter(
                                                  e => e.facialValue === ebon.facialValue
                                              ).length > 1;
                                          const baseLabel = `${vendor ||
                                              '...'} - ${formattedValue}€`;
                                          const label = showNumber
                                              ? `${index + 1} - ${baseLabel}`
                                              : baseLabel;

                                          return (
                                              <tr
                                                  key={`ebon-${ebon.id || index}`}
                                                  className="tableRowItem">
                                                  <td
                                                      style={{
                                                          maxWidth: '200px'
                                                      }}>
                                                      <span className="tdTextStyle">{label}</span>
                                                  </td>
                                                  <td>
                                                      <span className="tdTextStyle">
                                                          {formatAmountCurrency(ebon.facialValue)}
                                                      </span>
                                                  </td>
                                                  <td>
                                                      <span className="tdTextStyle">
                                                          {convertDate(ebon.expirationDate)}
                                                      </span>
                                                  </td>
                                                  <td>
                                                      <EbonStatusBadge
                                                          status={ebon.userState}
                                                          remainingBalance={ebon.remainingBalance}
                                                          totalAmount={ebon.facialValue}
                                                      />
                                                  </td>
                                                  {hasEbonLink(ebonList) && (
                                                      <td>
                                                          <span className="tdTextStyle ebonLink">
                                                              {ebon.ebonCodeDto &&
                                                                  ebon.ebonCodeDto.type ===
                                                                      EBON_TYPE_LINK_CODE && (
                                                                      <a
                                                                          href={convertHrefLink(
                                                                              ebon.ebonCodeDto
                                                                                  .field4
                                                                          )}
                                                                          target="_blank">
                                                                          {
                                                                              i18nPage.EBON
                                                                                  .LINK_ACTION
                                                                          }
                                                                      </a>
                                                                  )}
                                                          </span>
                                                      </td>
                                                  )}
                                              </tr>
                                          );
                                      })
                                    : null}
                            </tbody>
                            <tfoot>
                                <tr className="tableRowHeader">
                                    <th scope="row">
                                        <span className="tableHeaderStyle">
                                            {i18nPage.EBON.TOTAL}
                                        </span>
                                    </th>
                                    <td>
                                        <span className="tableHeaderStyle">
                                            {formatAmountCurrency(total.value)}
                                        </span>
                                    </td>
                                    <td colSpan={hasEbonLink(ebonList) ? 3 : 2}>
                                        <span className="tdTextStyle">-</span>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    {!isEnterpriseCard && stateOk && (
                        <EbonStatusUpdateForm
                            initialStatus={ebonStatus}
                            initialBalance={remainingBalance}
                            totalAmount={total.value}
                            ebonList={ebonList}
                            onSubmit={handleSubmitStatus}
                            disabled={submitting}
                        />
                    )}
                    {stateOk && (
                        <div className="card-button">
                            {!isEnterpriseCard && (
                                <div className="link-list">
                                    {ebonList && ebonList.length > 0 ? (
                                        getCodeActions(ebonList).map(({ label, action }, i) => (
                                            <ButtonComponent
                                                key={i}
                                                color="main"
                                                text={label}
                                                onClick={() => action()}
                                                disabled={fetchingCodes}
                                                ariaLabel={
                                                    fetchingCodes ? i18nPage.FETCHING_PDF : label
                                                }
                                            />
                                        ))
                                    ) : (
                                        <LoadingComponent />
                                    )}
                                </div>
                            )}
                            <div>
                                {!isEnterpriseCard && vendor && !paymentRequest && (
                                    <ButtonComponent
                                        color="main"
                                        text={i18nPage.DOWNLOAD_BILL}
                                        onClick={() => downloadBill(id)}
                                        disabled={fetchingBill}
                                        ariaLabel={
                                            fetchingBill
                                                ? i18nPage.FETCHING_PDF
                                                : i18nPage.DOWNLOAD_BILL
                                        }
                                    />
                                )}
                            </div>
                            {isEnterpriseCard && (
                                <div className="enterprise-card-how-to-button">
                                    <button
                                        id={`how-to-${displayOrderId}`}
                                        type="button"
                                        aria-expanded={showHowTo}
                                        aria-controls={`how-to-content-${displayOrderId}`}
                                        onClick={() => setshowHowTo(!showHowTo)}>
                                        {i18nPage.ENTERPRISE_CARD_HOW_TO}
                                        <SvgComponent
                                            src={`${STATIC_ROOT}/fleche.svg`}
                                            className={`chevron ${showHowTo ? 'haut' : 'bas'}`}
                                            width="20"
                                            height="20"
                                            aria-hidden="true"
                                            focusable="false"
                                        />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {isEnterpriseCard && showHowTo && (
                        <div id={`how-to-content-${displayOrderId}`}>
                            <div className="enterprise-card-how-to">
                                <EnterpriseCardSteps />
                            </div>
                            <div className="enterprise-card-explanation">
                                {convertHtmlToReactDomWithParams(
                                    i18nPage.ENTERPRISE_CARD_EXPLANATION,
                                    ENTERPRISE_CARD_NAME,
                                    BRAND_NAME_ALT
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

EbonOrderCard.propTypes = {
    fetchAccountEbonOrderByIdWithCode: PropTypes.func,
    updateEbonUserState: PropTypes.func,
    i18nPage: PropTypes.object.isRequired,
    items: PropTypes.array,
    id: PropTypes.string,
    partnerId: PropTypes.number,
    vendor: PropTypes.string,
    date: PropTypes.number,
    ebonList: PropTypes.array
};

const mapStateToProps = (state, ownProps) => {
    return {
        i18nPage: state.i18n.page.ACCOUNT.EBONS,
        BRAND_NAME_ALT: state.i18n.common.BRAND_NAME_ALT,
        ebonOrderWithCodes: searchCodes(state.account.myebons.ebonOrderWithCodes, ownProps.id),
        token: state.authentication.token
    };
};

const searchCodes = (codes, orderId) => {
    const filteredCodes = codes.filter(code => code.id === orderId);
    return filteredCodes.length > 0 ? filteredCodes[0] : {};
};

const mapDispatchToProps = {
    fetchAccountEbonOrderByIdWithCode,
    updateEbonUserState
};
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EbonOrderCard));



// WEBPACK FOOTER //
// ./src/components/pages/account/myebons/EbonOrderCard.js